import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '../lib/api';
import type {
  AdminCapabilities,
  AdminRoleDto,
  AdminUserDto,
  CreateInvitationRequest,
  CreateInvitationResultDto,
  CreateUserRequest,
  InvitationDto,
  InvitationStatus,
  PagedResult,
} from '../lib/types';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useCurrentUser } from '../hooks/useCurrentUser';

export function AdminUsers() {
  const isAdmin = useIsAdmin();
  const { data: me } = useCurrentUser();

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
        <h1 className="text-xl font-bold text-brand-charcoal">Forbidden</h1>
        <p className="text-slate-600 mt-2">You need the <code>Admin</code> role to view this page.</p>
      </div>
    );
  }

  return <AdminUsersInner currentEmail={me?.email ?? null} />;
}

function AdminUsersInner({ currentEmail }: { currentEmail: string | null }) {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const usersQ = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => apiClient.get<AdminUserDto[]>('/api/admin/users')
  });
  const rolesQ = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => apiClient.get<AdminRoleDto[]>('/api/admin/roles')
  });
  const capsQ = useQuery({
    queryKey: ['admin', 'capabilities'],
    queryFn: () => apiClient.get<AdminCapabilities>('/api/admin/users/capabilities')
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin'] });

  const setRoles = useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) =>
      apiClient.put<AdminUserDto>(`/api/admin/users/${id}/roles`, { roles }),
    onSuccess: invalidate
  });
  const toggleDisabled = useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      apiClient.put<AdminUserDto>(`/api/admin/users/${id}`, { disabled }),
    onSuccess: invalidate
  });
  const deleteUser = useMutation({
    mutationFn: (id: string) => apiClient.del<void>(`/api/admin/users/${id}`),
    onSuccess: invalidate
  });
  const resetPassword = useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
      apiClient.post<void>(`/api/admin/users/${id}/reset-password`, { newPassword }),
    onSuccess: invalidate
  });

  const supportsLocalAccounts = capsQ.data?.supportsLocalAccounts ?? false;
  const knownRoles = (rolesQ.data ?? []).map(r => r.name);
  const fallbackRoles = ['Admin', 'User'];
  const allRoles = knownRoles.length > 0 ? knownRoles : fallbackRoles;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-brand-charcoal">User administration</h1>
          <p className="text-slate-600 mt-1 text-sm">
            {supportsLocalAccounts
              ? 'Identity mode: full user management is available.'
              : 'IdP mode: managing local role assignments. The user store lives in your IdP.'}
          </p>
        </div>
        {supportsLocalAccounts && (
          <button
            onClick={() => setShowCreate(true)}
            className="ml-auto rounded-md bg-brand-blue text-white px-4 py-2 text-sm font-medium hover:bg-brand-blue/90"
          >
            New user
          </button>
        )}
        {!supportsLocalAccounts && (
          <button
            onClick={() => setShowCreate(true)}
            className="ml-auto rounded-md bg-brand-blue text-white px-4 py-2 text-sm font-medium hover:bg-brand-blue/90"
          >
            Grant role to email
          </button>
        )}
      </div>

      {usersQ.isLoading && <div className="text-slate-500">Loading users...</div>}
      {usersQ.error && <div className="text-red-600 text-sm">Failed to load users.</div>}

      {usersQ.data && (
        <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Roles</th>
                <th className="px-4 py-2">Source</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {usersQ.data.map(u => {
                const isSelf = !!currentEmail && u.email?.toLowerCase() === currentEmail.toLowerCase();
                return (
                  <tr key={u.id} className="border-t border-slate-200 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.email}</div>
                      {isSelf && <div className="text-xs text-brand-blue">(you)</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {allRoles.map(role => {
                          const has = u.roles.some(r => r.toLowerCase() === role.toLowerCase());
                          const disableSelfDemote = isSelf && role.toLowerCase() === 'admin' && has;
                          return (
                            <label key={role} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${has ? 'bg-brand-blue text-white border-brand-blue' : 'bg-slate-50 text-slate-700 border-slate-300'} ${disableSelfDemote ? 'opacity-50' : 'cursor-pointer'}`}>
                              <input
                                type="checkbox"
                                checked={has}
                                disabled={disableSelfDemote || setRoles.isPending}
                                onChange={() => {
                                  const next = has
                                    ? u.roles.filter(r => r.toLowerCase() !== role.toLowerCase())
                                    : [...u.roles, role];
                                  setRoles.mutate({ id: u.id, roles: next });
                                }}
                                className="hidden"
                              />
                              {role}
                            </label>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{u.source}</td>
                    <td className="px-4 py-3">
                      {u.disabled
                        ? <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Disabled</span>
                        : <span className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700">Active</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {supportsLocalAccounts && !isSelf && (
                        <button
                          onClick={() => {
                            const np = prompt(`New password for ${u.email}?`);
                            if (np) resetPassword.mutate({ id: u.id, newPassword: np });
                          }}
                          className="text-xs text-brand-blue hover:underline mr-3"
                        >
                          Reset password
                        </button>
                      )}
                      <button
                        disabled={isSelf || toggleDisabled.isPending}
                        onClick={() => toggleDisabled.mutate({ id: u.id, disabled: !u.disabled })}
                        className="text-xs text-slate-700 hover:underline mr-3 disabled:opacity-50"
                      >
                        {u.disabled ? 'Enable' : 'Disable'}
                      </button>
                      <button
                        disabled={isSelf || deleteUser.isPending}
                        onClick={() => {
                          if (confirm(`Delete ${u.email}? This cannot be undone.`)) deleteUser.mutate(u.id);
                        }}
                        className="text-xs text-red-600 hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {usersQ.data.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No users yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(setRoles.error || toggleDisabled.error || deleteUser.error || resetPassword.error) && (
        <div className="text-red-600 text-sm">
          {((setRoles.error || toggleDisabled.error || deleteUser.error || resetPassword.error) as ApiError | undefined)?.message
            ?? 'Action failed.'}
        </div>
      )}

      {showCreate && (
        <CreateUserDialog
          allRoles={allRoles}
          supportsLocalAccounts={supportsLocalAccounts}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); invalidate(); }}
        />
      )}

      <InvitationsPanel allRoles={allRoles} supportsLocalAccounts={supportsLocalAccounts} />
    </div>
  );
}

const INVITE_STATUS_PILL: Record<InvitationStatus, string> = {
  Pending: 'bg-amber-100 text-amber-700',
  Accepted: 'bg-emerald-100 text-emerald-700',
  Expired: 'bg-slate-100 text-slate-600',
  Revoked: 'bg-red-100 text-red-700',
};

/**
 * Issue and manage invite links. The generated URL is shown once, right after creation — the
 * server stores only a hash of the token, so it genuinely cannot be retrieved again.
 */
function InvitationsPanel({
  allRoles,
  supportsLocalAccounts,
}: {
  allRoles: string[];
  supportsLocalAccounts: boolean;
}) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<string[]>(['User']);
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [freshLink, setFreshLink] = useState<{ email: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const listQ = useQuery({
    queryKey: ['admin', 'invitations'],
    queryFn: () => apiClient.get<PagedResult<InvitationDto>>('/api/admin/invitations?pageSize=50'),
  });

  const create = useMutation({
    mutationFn: (req: CreateInvitationRequest) =>
      apiClient.post<CreateInvitationResultDto>('/api/admin/invitations', req),
    onSuccess: (result) => {
      setFreshLink({ email: result.invitation.email, url: result.inviteUrl });
      setCopied(false);
      setEmail('');
      setRoles(['User']);
      qc.invalidateQueries({ queryKey: ['admin', 'invitations'] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: number) => apiClient.del<void>(`/api/admin/invitations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'invitations'] }),
  });

  function toggleRole(role: string) {
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  }

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false); // Clipboard blocked (non-HTTPS origin) — the input is selectable as a fallback.
    }
  }

  if (!supportsLocalAccounts) {
    return (
      <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-brand-charcoal">Invite links</h2>
        <p className="text-sm text-slate-600 mt-2">
          Invite links create local accounts, so they're unavailable while the app authenticates
          against an external identity provider. Add the person in your IdP, then grant their role
          above once they've signed in.
        </p>
      </div>
    );
  }

  const createError = create.error as ApiError | undefined;

  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-brand-charcoal">Invite links</h2>
        <p className="text-sm text-slate-600 mt-1">
          Generate a single-use link and send it to the new user however you like. They set their
          own password — you never handle it.
        </p>
      </div>

      {freshLink && (
        <div className="border border-brand-orange/40 bg-brand-orange/5 rounded-lg p-4 space-y-2">
          <p className="text-sm font-medium text-brand-charcoal">
            Invite link for {freshLink.email} — copy it now.
          </p>
          <p className="text-xs text-brand-charcoal/70">
            This is the only time it can be shown. If you lose it, revoke the invite and issue a new one.
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={freshLink.url}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Invite link"
              className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs font-mono bg-white"
            />
            <button
              type="button"
              onClick={() => void copyLink(freshLink.url)}
              className="rounded bg-brand-blue text-white px-3 py-1 text-sm hover:bg-brand-blue/90"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={() => setFreshLink(null)}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate({ email: email.trim(), roles, expiresInDays });
        }}
      >
        <div>
          <label htmlFor="invite-email" className="block text-xs font-medium text-slate-600">Email</label>
          <input
            id="invite-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@alliancetg.com"
            className="mt-1 border border-slate-300 rounded px-2 py-1 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          />
        </div>

        <div>
          <span className="block text-xs font-medium text-slate-600">Roles</span>
          <div className="flex items-center gap-3 mt-2">
            {allRoles.map((r) => (
              <label key={r} className="flex items-center gap-1.5 text-sm text-brand-charcoal">
                <input type="checkbox" checked={roles.includes(r)} onChange={() => toggleRole(r)} />
                {r}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="invite-expiry" className="block text-xs font-medium text-slate-600">
            Expires in (days)
          </label>
          <input
            id="invite-expiry"
            type="number"
            min={1}
            max={90}
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(Number(e.target.value))}
            className="mt-1 border border-slate-300 rounded px-2 py-1 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          />
        </div>

        <button
          type="submit"
          disabled={create.isPending || email.trim().length === 0}
          className="rounded-md bg-brand-blue text-white px-4 py-2 text-sm font-medium hover:bg-brand-blue/90 disabled:opacity-50"
        >
          {create.isPending ? 'Generating…' : 'Generate link'}
        </button>
      </form>

      {createError && (
        <p role="alert" className="text-sm text-red-700">
          {createError.body && typeof createError.body === 'object' && 'error' in createError.body
            ? String((createError.body as { error: unknown }).error)
            : 'Could not create the invitation.'}
        </p>
      )}

      {listQ.isLoading && <p className="text-sm text-slate-500">Loading invitations…</p>}
      {listQ.error && <p className="text-sm text-red-600">Failed to load invitations.</p>}

      {listQ.data && listQ.data.items.length === 0 && (
        <p className="text-sm text-slate-500">No invitations yet.</p>
      )}

      {listQ.data && listQ.data.items.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="py-2 px-3">Email</th>
                <th className="py-2 px-3">Roles</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Expires</th>
                <th className="py-2 px-3">Invited by</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {listQ.data.items.map((inv) => (
                <tr key={inv.id} className="border-t border-slate-100">
                  <td className="py-2 px-3 font-medium text-brand-charcoal">{inv.email}</td>
                  <td className="py-2 px-3 text-slate-600">{inv.roles.join(', ')}</td>
                  <td className="py-2 px-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${INVITE_STATUS_PILL[inv.status]}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-slate-600">
                    {new Date(inv.expiresUtc).toLocaleDateString()}
                  </td>
                  <td className="py-2 px-3 text-slate-600">{inv.createdBy ?? '—'}</td>
                  <td className="py-2 px-3 text-right">
                    {inv.status === 'Pending' && (
                      <button
                        type="button"
                        onClick={() => revoke.mutate(inv.id)}
                        disabled={revoke.isPending}
                        className="text-xs text-red-600 hover:underline disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateUserDialog(props: {
  allRoles: string[];
  supportsLocalAccounts: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState<string[]>(['User']);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const body: CreateUserRequest = {
        email,
        password: props.supportsLocalAccounts ? password : '',
        roles
      };
      return apiClient.post<AdminUserDto>('/api/admin/users', body);
    },
    onSuccess: props.onCreated,
    onError: (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Create failed')
  });

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center" onClick={props.onClose}>
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-brand-charcoal mb-4">
          {props.supportsLocalAccounts ? 'Create user' : 'Grant role to email'}
        </h2>
        <form
          onSubmit={e => { e.preventDefault(); setErr(null); create.mutate(); }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email" required value={email} onChange={e => setEmail(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
            />
          </div>
          {props.supportsLocalAccounts && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Initial password</label>
              <input
                type="password" required value={password} onChange={e => setPassword(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Roles</label>
            <div className="flex flex-wrap gap-2">
              {props.allRoles.map(r => {
                const has = roles.includes(r);
                return (
                  <label key={r} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border cursor-pointer ${has ? 'bg-brand-blue text-white border-brand-blue' : 'bg-slate-50 text-slate-700 border-slate-300'}`}>
                    <input
                      type="checkbox" checked={has} className="hidden"
                      onChange={() => setRoles(has ? roles.filter(x => x !== r) : [...roles, r])}
                    />
                    {r}
                  </label>
                );
              })}
            </div>
          </div>
          {err && <div className="text-red-600 text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={props.onClose} className="px-3 py-2 text-sm text-slate-700 hover:underline">Cancel</button>
            <button
              type="submit" disabled={create.isPending}
              className="rounded-md bg-brand-blue text-white px-4 py-2 text-sm font-medium hover:bg-brand-blue/90 disabled:opacity-50"
            >
              {create.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
